// @flow
import * as React from 'react';
import styled, {createGlobalStyle} from 'styled-components';
import { useNavigate, useParams } from "react-router-dom";
import {useLayoutState, LayoutWrapper} from 'components/Layout';
import { useSessionState, useSessionDispatch } from 'components/Session';
import {ShowroomMode, PreloadAssets} from 'components/Constants';
import {imageExists} from 'components/Image';
import Settings from 'config/Settings';
import Loading from '../loading/Loading';
import Header from './Header';
import Configurator from './Configurator';
import { enableSpots, resizeVR, addVREventListener, removeVREventListener } from 'utils/vrUtils';

type Props = {};

export default function Showroom(props: Props): React.Element<any> {
  const layoutState = useLayoutState();
  const sessionState = useSessionState();
  const sessionDispatch = useSessionDispatch();
  const navigate = useNavigate();
  const params = useParams();

  const [showLoading, setShowLoading] = React.useState(false);
  const [closingLoading, setClosingLoading] = React.useState(false);
  const closingDuraion = 0;

  const [fullscreen, setFullscreen] = React.useState(false);
  const [portraitMode, setPortraitMode] = React.useState(layoutState.portrait);

  const [infoMode, setInfoMode] = React.useState(ShowroomMode.NONE);
  const [openSidebar, setOpenSidebar] = React.useState(false);
  const [closingSidebar, setClosingSidebar] = React.useState(false);
  const [expandSidebar, setExpandSidebar] = React.useState(false);
  //const [openFeature, setOpenFeature] = React.useState(-1);
  const featureCodes = React.useMemo(() => infoMode === ShowroomMode.NONE ? [] : Settings[`${infoMode}InfoCodes`]?.[sessionState.modelIndex], [infoMode, sessionState]);
  const sidebarWidth = React.useMemo(() => {
    if (layoutState.width > 1280) {
      return layoutState.scale * 475;
    }
    else {
      return 336;
    }
  }, [layoutState]);

  const handleInfo = React.useCallback((info: string) => {
    setInfoMode(info);
    enableSpots(info === ShowroomMode.EXTERIOR || info === ShowroomMode.INTERIOR);
    // if (info === ShowroomMode.EXTERIOR || info === ShowroomMode.INTERIOR) {

    //   const nextFeatureCodes = Settings[info + "InfoCodes"][sessionState.modelIndex];
    //   selectSpots(nextFeatureCodes[0]);
    // }
  }, []);

  const handleSidebar = React.useCallback((flag: boolean) => {
    if (!flag) {
      setExpandSidebar(false);
      setClosingSidebar(openSidebar);
      setTimeout(() => {
        setClosingSidebar(false);
        setOpenSidebar(flag);
        window.scrollTo(0, 0);
      }, 250);

      // close
      if (true) {
        resizeVR(layoutState.width, layoutState.height);
      }
    }
    else {
      setClosingSidebar(false);
      setOpenSidebar(flag);

      // open
      if (true) {
      if (layoutState.portrait) {
        resizeVR(layoutState.width, layoutState.height * 0.5);
      }
      else {
        resizeVR(layoutState.width - sidebarWidth * layoutState.scale, layoutState.height);
      }
      }
    }
  }, [openSidebar, layoutState, sidebarWidth]);

  // const handleFeature = React.useCallback((idx: number) => {
  //   if (idx !== openFeature) {
  //     setOpenFeature(idx);
  //     //console.log("featureCodes", featureCodes, "idx", idx);
  //     selectSpots(featureCodes[idx]);
  //   }
  // }, [openFeature, featureCodes]);

  const handleCompleteLoading = React.useCallback(() => {
    setClosingLoading(true);
    setTimeout(() => {setShowLoading(false); setClosingLoading(false);}, closingDuraion * 1000);
  }, []);

  React.useEffect(() => {
    if (sessionState.modelIndex === -1) {
      sessionDispatch({type: 'SELECT_MODEL', data: Settings.models[0]});
      setShowLoading(true);
    }
  }, [navigate, params.model, sessionState, sessionDispatch]);

  React.useEffect(() => {
    function handleSpot(spot: string) {
      const spotIdx = featureCodes.indexOf(spot);
      if (spotIdx >= 0) {
        //setOpenFeature(spotIdx);
        handleSidebar(true);
      }
    }

    addVREventListener("car:spot", handleSpot);
    return () => {
      removeVREventListener("car:spot", handleSpot);
    }
  }, [featureCodes, handleSidebar]);

  React.useEffect(() => {
    if (!openSidebar) {
      resizeVR(layoutState.width, layoutState.height);
    }
    setPortraitMode(layoutState.portrait);
  }, [layoutState, openSidebar]);

  React.useEffect(() => {
    PreloadAssets.forEach((name) => {
      new Image().src = imageExists(name);
    });
  }, []);

  return <Wrapper dataOpen={openSidebar} dataClosing={closingSidebar} sidebarWidth={sidebarWidth} portraitMode={portraitMode}>
      <RootOverrideStyle dataOpen={openSidebar} dataClosing={closingSidebar} sidebarWidth={sidebarWidth} />
      <DivContents className={openSidebar && expandSidebar ? "main-contents-zero" : "main-contents"}>
        <header>
          <Header hidden={openSidebar} dataFullScreen={fullscreen} onOpenSelection={() => {}} />
        </header>
        <main>
          <Configurator onInfo={handleInfo} selectedInfo={infoMode !== ShowroomMode.NONE} openSidebar={openSidebar} fullscreen={fullscreen} onFullscreen={(flag) => setFullscreen(flag)} />
        </main>
      </DivContents>
      <DivSidebar className={openSidebar && expandSidebar ? "main-sidebar-full" : "main-sidebar"}>
      </DivSidebar>
      {(showLoading) && <Loading onComplete={handleCompleteLoading} closing={closingLoading} closingDuration={closingDuraion} />}
    </Wrapper>;
}

const RootOverrideStyle = createGlobalStyle`
  #root-canvas {
    transition: all ${(props: any) => props.theme.defaults.transitionDuration}s ease-out;

    margin-right: ${(props: any) => props.theme.layout.portrait ? undefined : (props.dataOpen && !props.dataClosing ? `${props.sidebarWidth}px` : '0px')};
    height: ${(props: any) => props.theme.layout.portrait ? (props.dataOpen && !props.dataClosing ? `${props.theme.layout.height * 0.5}px` : undefined) : undefined};
  }
`

const Wrapper = styled(LayoutWrapper)`
  overflow: hidden;
  display: flex;
  flex-direction: ${(props: any) => props.theme.layout.portrait ? 'column' : 'row'};

  > .main-contents {
    width: ${(props: any) => props.theme.layout.portrait ? undefined : (props.dataOpen && !props.dataClosing ? `${props.theme.layout.width - (props.sidebarWidth * props.theme.layout.scale)}px` : '100%')};
    height: ${(props: any) => props.theme.layout.portrait ? (props.dataOpen && !props.dataClosing ? `${props.theme.layout.height * 0.5}px` : '100%') : undefined};
    display: flex;
    transition: all ${(props: any) => props.theme.defaults.transitionDuration}s ease-out;

    > main {
      flex: 1;
    }
  }

  > .main-contents-zero {
    width: 100%;
    height: 0px;
    transition: all ${(props: any) => props.theme.defaults.transitionDuration}s ease-out;
  }

  > .main-sidebar {
    width: ${(props: any) => props.theme.layout.portrait ? undefined : (props.dataOpen && !props.dataClosing ? `${props.sidebarWidth * props.theme.layout.scale}px` : '0px')};
    height: ${(props: any) => props.theme.layout.portrait ? (props.dataOpen && !props.dataClosing ? `${props.theme.layout.height * 0.5}px` : '0px') : undefined};
    position: relative;
    left: 0px;
    bottom: 0px;
    transition: all ${(props: any) => props.theme.defaults.transitionDuration}s ease-out;

    > div {
      width: ${(props: any) => props.theme.layout.portrait ? undefined : `${props.sidebarWidth * props.theme.layout.scale}px`};
      height: ${(props: any) => props.theme.layout.portrait ? (props.dataExpand ? '100%' : `${props.theme.layout.height * 0.5}px`) : undefined};
    }
  }

  > .main-sidebar-full {
    height: 100%;
    position: relative;
    left: 0px;
    bottom: 0px;
    transition: all ${(props: any) => props.theme.defaults.transitionDuration}s ease-out;

    > div {
      height: 100%;
    }
  }
`

const DivContents = styled.div`
  display: flex;
  flex-direction: column;
`

const DivSidebar = styled.div`
  background-color: #ffffff;
  position: relative;
`
